type FranchiseIdentitySource = {
  id?: string | null;
  franchiseId?: string | null;
  nft?: { tokenId?: string | null; imageVersion?: string | null } | null;
};

type FranchiseImageBinding = NonNullable<FranchiseIdentitySource['nft']>;

/** Return the immutable token-backed id for a franchise-like record. */
export function getFranchiseId(team: FranchiseIdentitySource): string {
  return String(
    team.nft?.tokenId
    || team.franchiseId
    || team.id
    || '',
  ).trim();
}

/**
 * Resolve a stable team collection image to its immutable content-revision
 * object. Custom branding art is left alone. Older records without a revision
 * continue to use the stable `<tokenId>.png` path.
 */
export function versionFranchiseImageSrc(
  src: string,
  nft: FranchiseImageBinding | null | undefined,
): string {
  const tokenId = String(nft?.tokenId || '').trim();
  const imageVersion = String(nft?.imageVersion || '').trim();
  if (!src || !tokenId || !imageVersion) {
    return src;
  }

  const hashIndex = src.indexOf('#');
  const hash = hashIndex >= 0 ? src.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? src.slice(0, hashIndex) : src;
  const queryIndex = withoutHash.indexOf('?');
  const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const encodedTokenId = encodeURIComponent(tokenId);
  const collectionRoot = `collection/teams/images/${encodedTokenId}`;
  const collectionIndex = path.indexOf(collectionRoot);
  if (collectionIndex < 0) {
    return src;
  }

  const suffix = path.slice(collectionIndex + collectionRoot.length);
  if (suffix !== '.png' && !/^\/[^/]+\.png$/i.test(suffix)) {
    return src;
  }

  return `${path.slice(0, collectionIndex)}${collectionRoot}/${encodeURIComponent(imageVersion)}.png${hash}`;
}

// The NFT token id is the only franchise identifier — abbreviations and legacy
// catalog ids are display data, never addresses. Canonicalization is therefore
// a trim-only normalization.
export function canonicalTeamId(teamId: string | null | undefined): string {
  return String(teamId || '').trim();
}

export function canonicalGameId(gameId: string | null | undefined): string {
  const value = String(gameId || '').trim();
  if (!value) {
    return value;
  }
  const regularSeasonMatch = value.match(/^(.*-WK\d+)-([^-]+)-([^-]+)$/i);
  if (!regularSeasonMatch) {
    return value;
  }
  const [, prefix, away, home] = regularSeasonMatch;
  return `${prefix}-${canonicalTeamId(away)}-${canonicalTeamId(home)}`;
}

export function canonicalPlayerId(playerId: string | null | undefined): string {
  return String(playerId || '').trim();
}
